import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { User, Calendar, CreditCard, FileText, Mail, Phone, StickyNote, ArrowLeft } from 'lucide-react'
import { customersApi } from '../../lib/api/customers'
import { appointmentsApi, Appointment } from '../../lib/api/appointments'
import { Badge, Alert } from '../../components/common'

export const Route = createFileRoute('/dashboard/customers/$id')({
  component: CustomerDetailComponent,
})

type TabType = 'details' | 'appointments' | 'insurance' | 'payments'

function CustomerDetailComponent() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<TabType>('details')
  const [appointmentFilter, setAppointmentFilter] = useState<'all' | 'upcoming' | 'completed' | 'cancelled'>('all')

  const { data: customerData, isLoading: customerLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.getOne(Number(id)),
  })

  const { data: appointmentsData, isLoading: appointmentsLoading } = useQuery({
    queryKey: ['customer-appointments', id],
    queryFn: () => appointmentsApi.getAll({ 
      customer_id: Number(id),
      start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    }),
  })

  const customer = customerData?.customer
  const appointments = appointmentsData?.appointments || []

  const upcomingAppointments = appointments.filter(
    (apt) => apt.status === 'scheduled' || apt.status === 'confirmed'
  )
  const completedAppointments = appointments.filter((apt) => apt.status === 'completed')
  const cancelledAppointments = appointments.filter((apt) => apt.status === 'cancelled')

  const filteredAppointments = appointmentFilter === 'all' 
    ? appointments
    : appointmentFilter === 'upcoming'
    ? upcomingAppointments
    : appointmentFilter === 'completed'
    ? completedAppointments
    : cancelledAppointments

  const groupAppointmentsByDate = (apts: Appointment[]) => {
    return apts.reduce((acc, apt) => {
      if (!acc[apt.date]) {
        acc[apt.date] = []
      }
      acc[apt.date].push(apt)
      return acc
    }, {} as Record<string, Appointment[]>)
  }

  if (customerLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="alert alert-error">
        <span>Customer not found</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/dashboard/customers"
        className="btn btn-ghost gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Customers
      </Link>

      {/* Header */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="flex items-center gap-4">
            <div className="avatar placeholder">
              <div className="bg-accent text-white rounded-full w-16">
                <span className="text-2xl">{customer.name.charAt(0).toUpperCase()}</span>
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">{customer.name}</h1>
              <div className="flex gap-4 mt-2 text-sm text-base-content/70">
                {customer.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <span>{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <span>{customer.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs tabs-boxed bg-base-100 shadow-xl p-2">
        <button
          className={`tab ${activeTab === 'details' ? '!bg-accent !text-white' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <User className="w-4 h-4 mr-2" />
          Details
        </button>
        <button
          className={`tab ${activeTab === 'appointments' ? '!bg-accent !text-white' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Appointments
        </button>
        <button
          className={`tab ${activeTab === 'insurance' ? '!bg-accent !text-white' : ''}`}
          onClick={() => setActiveTab('insurance')}
        >
          <FileText className="w-4 h-4 mr-2" />
          Insurance
        </button>
        <button
          className={`tab ${activeTab === 'payments' ? '!bg-accent !text-white' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Payments
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Name</span>
                </label>
                <p className="text-base-content">{customer.name}</p>
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Email</span>
                </label>
                <p className="text-base-content">{customer.email || '-'}</p>
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Phone</span>
                </label>
                <p className="text-base-content">{customer.phone || '-'}</p>
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Customer Since</span>
                </label>
                <p className="text-base-content">
                  {new Date(customer.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            {customer.notes && (
              <div className="mt-4">
                <label className="label">
                  <span className="label-text font-semibold flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notes
                  </span>
                </label>
                <div className="bg-base-200 rounded-lg p-4">
                  <p className="text-base-content whitespace-pre-wrap">{customer.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="stat">
                  <div className="stat-title">Total Appointments</div>
                  <div className="stat-value">{appointments.length}</div>
                </div>
              </div>
            </div>
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="stat">
                  <div className="stat-title">Completed</div>
                  <div className="stat-value text-success">{completedAppointments.length}</div>
                </div>
              </div>
            </div>
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="stat">
                  <div className="stat-title">Upcoming</div>
                  <div className="stat-value text-info">{upcomingAppointments.length}</div>
                </div>
              </div>
            </div>
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="stat">
                  <div className="stat-title">Cancelled</div>
                  <div className="stat-value text-error">{cancelledAppointments.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Appointment History */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <h2 className="card-title">Appointment History</h2>
                <div className="tabs tabs-boxed">
                  <button
                    className={`tab ${appointmentFilter === 'all' ? '!bg-accent !text-white' : ''}`}
                    onClick={() => setAppointmentFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`tab ${appointmentFilter === 'upcoming' ? '!bg-accent !text-white' : ''}`}
                    onClick={() => setAppointmentFilter('upcoming')}
                  >
                    Upcoming
                  </button>
                  <button
                    className={`tab ${appointmentFilter === 'completed' ? '!bg-accent !text-white' : ''}`}
                    onClick={() => setAppointmentFilter('completed')}
                  >
                    Completed
                  </button>
                  <button
                    className={`tab ${appointmentFilter === 'cancelled' ? '!bg-accent !text-white' : ''}`}
                    onClick={() => setAppointmentFilter('cancelled')}
                  >
                    Cancelled
                  </button>
                </div>
              </div>
              {appointmentsLoading ? (
                <div className="text-center py-8">
                  <span className="loading loading-spinner loading-lg"></span>
                </div>
              ) : filteredAppointments.length === 0 ? (
                <p className="text-center text-base-content/50 py-8">No appointments found</p>
              ) : (
                <div className="space-y-4 mt-4">
                  {Object.entries(groupAppointmentsByDate(filteredAppointments))
                    .sort(([dateA], [dateB]) => 
                      appointmentFilter === 'upcoming' 
                        ? dateA.localeCompare(dateB)
                        : dateB.localeCompare(dateA)
                    )
                    .map(([date, dateAppointments]) => (
                      <div key={date} className="border-l-4 border-accent pl-4">
                        <h3 className="font-semibold text-lg mb-2">
                          {new Date(date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </h3>
                        <div className="space-y-2">
                          {dateAppointments
                            .sort((a, b) => a.start_time.localeCompare(b.start_time))
                            .map((apt) => (
                              <div
                                key={apt.id}
                                className="bg-base-200 rounded-lg p-4 flex justify-between items-center"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">{apt.skill.name}</span>
                                    <Badge
                                      variant={
                                        apt.status === 'completed'
                                          ? 'success'
                                          : apt.status === 'cancelled'
                                          ? 'error'
                                          : apt.status === 'confirmed'
                                          ? 'info'
                                          : 'default'
                                      }
                                      size="sm"
                                    >
                                      {apt.status}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-base-content/70 mt-1">
                                    Provider: {
                                      apt.team_member.user.full_name || 
                                      (apt.team_member.user.first_name && apt.team_member.user.last_name 
                                        ? `${apt.team_member.user.first_name} ${apt.team_member.user.last_name}`
                                        : apt.team_member.user.first_name || apt.team_member.user.last_name || apt.team_member.user.email)
                                    }
                                  </p>
                                  <p className="text-sm text-base-content/70">
                                    Time: {apt.start_time.substring(0, 5)} - {apt.end_time.substring(0, 5)}
                                  </p>
                                  {apt.notes && (
                                    <p className="text-sm text-base-content/60 mt-2 italic">
                                      Note: {apt.notes}
                                    </p>
                                  )}
                                  {apt.completed_notes && (
                                    <div className="mt-2 p-3 bg-success/10 rounded-lg border-l-4 border-success">
                                      <p className="text-sm font-semibold text-success mb-1">Therapy Notes:</p>
                                      <p className="text-sm text-base-content/80">{apt.completed_notes}</p>
                                    </div>
                                  )}
                                  {apt.cancelled_notes && (
                                    <div className="mt-2 p-3 bg-error/10 rounded-lg border-l-4 border-error">
                                      <p className="text-sm font-semibold text-error mb-1">Cancellation Reason:</p>
                                      <p className="text-sm text-base-content/80">{apt.cancelled_notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'insurance' && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Insurance & Claims</h2>
            <Alert icon={<FileText className="w-5 h-5" />} variant="info" className="mt-4">
              Insurance and claims management will be available in a future update. This feature
              will include insurance policies, benefits tracking, and claim submission.
            </Alert>
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Payment History</h2>
            <Alert icon={<CreditCard className="w-5 h-5" />} variant="info" className="mt-4">
              Payment tracking will be available in a future update. This feature will include
              payment history, outstanding balances, and transaction details.
            </Alert>
          </div>
        </div>
      )}
    </div>
  )
}
